import Button from '@aurora/shared-client/components/common/Button/Button';
import {
  ButtonVariant,
  LoadingButtonVariant
} from '@aurora/shared-client/components/common/Button/enums';
import ConfirmationDialog from '@aurora/shared-client/components/common/ConfirmationDialog/ConfirmationDialog';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import type { AppContextInterface } from '@aurora/shared-client/components/context/AppContext/AppContext';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMessagePolicies from '@aurora/shared-client/components/messages/useMessagePolicies';
import useNodePolicies from '@aurora/shared-client/components/nodes/useNodePolicies';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import { hasErrors } from '@aurora/shared-client/helpers/apollo/ApolloHelper';
import { canModerateNode } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import Icons from '@aurora/shared-client/public/static/graphics/processed/enums';
import type {
  ManageContentPageAndParams,
  MessagePageOrReplyPageAndParams,
  MessagePagesAndParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  BlogTopicMessage,
  Discussable,
  Message,
  ReplyMessage,
  TkbTopicMessage
} from '@aurora/shared-generated/types/graphql-schema-types';
import { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import IdConverter from '@aurora/shared-utils/graphql/IdConverter/IdConverter';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import UrlBuilder from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlBuilder';
import UrlHelper from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper';
import React, { useCallback, useContext, useId, useState } from 'react';
import { OverlayTrigger, Popover, PopoverContent, useClassNameMapper } from 'react-bootstrap';
import ConversationStyleBehaviorHelper from '../../../helpers/boards/ConversationStyleBehaviorHelper';
import type {
  ClearMovePlaceholderForMessageMutation,
  ClearMovePlaceholderForMessageMutationVariables,
  DeleteMessageMutation,
  DeleteMessageMutationVariables,
  MessageViewFragment
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import deleteMessageMutation from '../MessageActionDelete/DeleteMessage.mutation.graphql';
import { updatedMessageForDeepLinkVar } from '../MessageDeepLink/MessageReactVarHelper';
import { MessageListTabItem } from '../MessageListTabs/MessageListTabs';
import messageRepliesQuery from '../MessageReplies.query.graphql';
import clearMovePlaceholder from './ClearMovePlaceholderForMessage.mutation.graphql';
import localStyles from './MessageCommentBanner.module.pcss';

interface Props {
  /**
   * Message to check author property
   */
  message: MessageViewFragment;
  /**
   * Whether to display moderation banner or not.
   */
  useModerationBanner?: boolean;
  /**
   * Whether to show the move comment banner.
   */
  useMoveCommentBanner?: boolean;
  /**
   * Whether the message is a placeholder at source.
   */
  isMoveMessagePlaceholderAtSource?: boolean;
  /**
   * The classname for move banner
   */
  moveBannerClassName?: string;
}

/**
 * Renders the banner bar for a message comment.
 */
const MessageCommentBanner: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  useModerationBanner = false,
  useMoveCommentBanner = false,
  isMoveMessagePlaceholderAtSource,
  moveBannerClassName
}) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser, community, contextMessage } = useContext<AppContextInterface>(AppContext);
  const [showClearPlaceholderModal, setShowClearPlaceholderModal] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const uid = useId();
  const { addToast } = useToasts();
  const tenant = useContext(TenantContext);
  const {
    formatMessage,
    loading: textLoading,
    FormattedMessage
  } = useTranslation(EndUserComponent.MESSAGE_COMMENT_BANNER);

  const { router, Link } = useEndUserRoutes();

  const { data: nodePolicies, loading: nodePoliciesLoading } = useNodePolicies(module, {
    id: community.id,
    useCanModerateNode: true
  });

  const { id, board } = message;
  const isBlog = board?.conversationStyle === ConversationStyle.Blog;
  const isTkb = board?.conversationStyle === ConversationStyle.Tkb;

  const [clearMovePlaceholderForMessage] = useMutationWithTracing<
    ClearMovePlaceholderForMessageMutation,
    ClearMovePlaceholderForMessageMutationVariables
  >(module, clearMovePlaceholder);

  const [deletePlaceholderMessageAtSource] = useMutationWithTracing<
    DeleteMessageMutation,
    DeleteMessageMutationVariables
  >(module, deleteMessageMutation);

  /**
   * Renders an error alert and logs GraphQL errors
   *
   * @param graphQLErrors graphql errors
   */
  const renderErrorFeedback = useCallback((): void => {
    const toastProps: ToastProps = {
      toastVariant: ToastVariant.BANNER,
      alertVariant: ToastAlertVariant.DANGER,
      title: formatMessage('failureTitle'),
      message: formatMessage('failureMessage'),
      autohide: true,
      delay: 4000,
      id: uid
    };
    addToast(toastProps);
  }, [addToast, formatMessage, uid]);

  const { data: messagePoliciesData, loading: messagePoliciesLoading } = useMessagePolicies(
    module,
    {
      id: id,
      useCanDoAuthoringActionsOnBlog: isBlog,
      useCanDoAuthoringActionsOnTkb: isTkb,
      useCanMoveMessage: true
    },
    !message || IdConverter.isOptimistic(message?.id)
  );

  if (nodePoliciesLoading || textLoading || messagePoliciesLoading) {
    return null;
  }

  const canMove = checkPolicy(messagePoliciesData?.message?.messagePolicies?.canMoveMessage);
  const canAccessModerationManagerPolicy = canModerateNode(nodePolicies?.coreNode);
  const canDoAuthoringActions = isBlog
    ? checkPolicy(
        (messagePoliciesData?.message as BlogTopicMessage)?.blogMessagePolicies
          ?.canDoAuthoringActionsOnBlog
      )
    : isTkb
    ? checkPolicy(
        (messagePoliciesData?.message as TkbTopicMessage)?.tkbMessagePolicies
          ?.canDoAuthoringActionsOnTkb
      )
    : authUser?.id === message?.author?.id;

  const queryParams = {
    [EndUserQueryParams.TAB]: router.getUnwrappedQueryParam(
      EndUserQueryParams.TAB,
      MessageListTabItem.MODERATION
    )
  };

  /**
   * Renders the link to spam dashboard.
   */
  function renderDashboardLink(): React.ReactElement {
    return (
      canAccessModerationManagerPolicy && (
        <Link<ManageContentPageAndParams>
          route={EndUserPages.ManageContentPage}
          query={queryParams}
          legacyBehavior={true}
        >
          {formatMessage('manageModeration')}
        </Link>
      )
    );
  }

  /**
   * Gets the URL for the moved message.
   */
  function getMovedMessageUrl(): string {
    let relativeUrlForRoute: string;
    const { messagePage } = ConversationStyleBehaviorHelper.getInstance(message.board);
    if ((message.originalMessageForPlaceholder as ReplyMessage)?.parent) {
      const originalMessageParent = (message.originalMessageForPlaceholder as ReplyMessage)
        .parent as Discussable;
      relativeUrlForRoute = router.getRelativeUrlForRoute<MessagePagesAndParams>(messagePage, {
        boardId: message.board.displayId,
        messageSubject: UrlHelper.determineSlugForMessagePath(originalMessageParent),
        messageId: originalMessageParent.uid.toString()
      });
    } else {
      const originalMessage = message.originalMessageForPlaceholder as Discussable;
      relativeUrlForRoute = router.getRelativeUrlForRoute<MessagePageOrReplyPageAndParams>(
        messagePage,
        {
          boardId: message.board.displayId,
          messageSubject: UrlHelper.determineSlugForMessagePath(originalMessage),
          messageId: originalMessage.uid.toString()
        }
      );
    }
    return UrlBuilder.fromUrl(tenant.baseUrl).addPath(relativeUrlForRoute).build();
  }

  /**
   * Invokes the clearMovePlaceholder mutation, renders an alert if there are any errors.
   *
   * @returns void
   */
  async function onClearMovePlaceholderForMessage() {
    const { data, errors } = await clearMovePlaceholderForMessage({
      variables: {
        messageId: id
      },
      refetchQueries: [
        {
          query: messageRepliesQuery,
          variables: {
            id: contextMessage.id
          }
        }
      ],
      onCompleted: (d): void => {
        if (!hasErrors(d.clearMovePlaceholderForMessage)) {
          updatedMessageForDeepLinkVar({
            ...message,
            showMoveIndicator: false
          } as Message);
        }
      }
    });

    if (errors || data?.clearMovePlaceholderForMessage?.errors) {
      renderErrorFeedback();
    }
    setShowClearPlaceholderModal(false);
  }

  /**
   * Invokes the deletePlaceholderMessageAtSource mutation, renders an alert if there are any errors.
   *
   * @returns void
   */
  async function onClearMovePlaceholderForMessageAtSource() {
    const { data, errors } = await deletePlaceholderMessageAtSource({
      variables: {
        id: id
      },
      refetchQueries: [
        {
          query: messageRepliesQuery,
          variables: {
            id: contextMessage.id
          }
        }
      ]
    });

    if (errors || !data?.deleteMessage?.result) {
      renderErrorFeedback();
    }
    setShowClearPlaceholderModal(false);
  }

  /**
   * Renders the popover displayed when a user focuses/hovers on button
   */
  function renderPopover(): React.ReactElement {
    return (
      <Popover id="clearPlaceholder" data-testid="clearPlaceholder.Popover">
        <PopoverContent>{formatMessage('clearPlaceholder.popover')}</PopoverContent>
      </Popover>
    );
  }

  /**
   * Render clear placeholder dialog
   *
   * @returns React.ReactElement
   */
  const renderClearPlaceholderDialog = (): React.ReactElement => {
    return (
      <ConfirmationDialog
        show={showClearPlaceholderModal}
        onHide={(): void => setShowClearPlaceholderModal(false)}
        onSubmit={() =>
          isMoveMessagePlaceholderAtSource
            ? onClearMovePlaceholderForMessageAtSource()
            : onClearMovePlaceholderForMessage()
        }
        onCancel={(): void => setShowClearPlaceholderModal(false)}
        titleText={formatMessage('confirmClearPlaceholderDialog.title')}
        bodyText={formatMessage('confirmClearPlaceholderDialog.body')}
        submitButtonType={LoadingButtonVariant.LOADING_BUTTON}
        submitButtonText={formatMessage('confirmClearPlaceholderDialog.submit')}
        cancelButtonText={formatMessage('confirmClearPlaceholderDialog.cancel')}
      />
    );
  };

  return (
    <>
      {useModerationBanner && (
        <div className={cx('lia-banner')} data-testid="MessageBannerReply">
          <span>
            {formatMessage(
              `${canDoAuthoringActions ? 'preModCommentAuthorText' : 'preModCommentModeratorText'}`
            )}
          </span>
          {renderDashboardLink()}
        </div>
      )}
      {useMoveCommentBanner && (
        <>
          <div
            className={cx('lia-moved-banner', moveBannerClassName)}
            data-testid="MovedComment"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span>
              <Icon icon={Icons.InfoIcon} color={IconColor.BLACK} size={IconSize.PX_12} />
            </span>
            <span>
              {isMoveMessagePlaceholderAtSource ? (
                <span className={cx('lia-moved')}>
                  <FormattedMessage
                    id={'movedMessagePlaceholder'}
                    values={{
                      discussionStyle: message.board.conversationStyle,
                      a: chunks => <a href={getMovedMessageUrl()}>{chunks}</a>
                    }}
                  />
                </span>
              ) : (
                formatMessage('movedComment', { discussionStyle: message.board.conversationStyle })
              )}
            </span>
            {isHovered && canMove && (
              <OverlayTrigger overlay={renderPopover()}>
                <Button
                  className={cx('lia-g-icon-btn lia-close-button')}
                  variant={ButtonVariant.NO_VARIANT}
                  onClick={() => setShowClearPlaceholderModal(true)}
                >
                  <Icon icon={Icons.CloseIcon} color={IconColor.BLACK} size={IconSize.PX_12} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
          {showClearPlaceholderModal && renderClearPlaceholderDialog()}
        </>
      )}
    </>
  );
};

export default MessageCommentBanner;
